import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';

@Component({
  selector: 'ppl-date-picker',
  standalone: true,
  imports: [CalendarModule, FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements OnDestroy {
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() placeholder = 'MM/DD/YYYY';
  @Input() ariaLabel = '';
  @Input() disableControl = false;
  @Input() showIcon = true;
  @Input() dateFormat = 'mm/dd/yy';
  @Input() iconDisplay: any;
  @Input() panelStyleClass = '';
  @Input() readonlyInput = true;
  @Input() monthNavigator = true;
  @Input() yearNavigator = true;
  @Input() yearRange: any;
  @Input() styleClass?: string;
  @Output() valueChange = new EventEmitter<any>();
  private currentFocusIndex = 0;
  private focusableElements: HTMLElement[] = [];
  value: any;
  private calendarElement?: HTMLElement;
  private buttonListeners: Map<
    Element,
    { click: EventListener; keydown: EventListener }
  > = new Map();
  private cellListeners: Map<
    Element,
    { click: EventListener; keydown: EventListener }
  > = new Map();
  private currentView: 'date' | 'month' | 'year' = 'date';
  constructor(private elementRef: ElementRef) {}

  setupCalendar() {
    const input = this.elementRef.nativeElement.querySelector(
      'input'
    ) as HTMLElement;
    if (input) {
      input.setAttribute(
        'aria-label',
        this.ariaLabel.length > 0 ? this.ariaLabel : this.placeholder
      );
    }
    // Wait for PrimeNG to render the calendar
    setTimeout(() => {
      this.onCalendarShow();
    }, 100);
  }

  private onCalendarShow() {
    this.calendarElement = document.querySelector(
      '.p-datepicker'
    ) as HTMLElement;
    if (!this.calendarElement) return;
    // Set role to application to make sure the container is on forms mode for screen readers
    this.calendarElement.setAttribute('role', 'application');
    this.attachButtonListeners();
    this.setupGridNavigation();
  }

  onCalendarClose() {
    this.cleanup();
  }

  private cleanup() {
    this.cleanupButtonListeners();
    this.cleanupCellListeners();
    this.calendarElement = undefined;
    this.removeLiveRegion();
  }

  private attachButtonListeners() {
    // Clean up old listeners first
    this.cleanupButtonListeners();

    // Find all navigation buttons
    const buttons = this.calendarElement!.querySelectorAll(
      '.p-datepicker-prev, .p-datepicker-next, .p-datepicker-month, .p-datepicker-year'
    );

    buttons.forEach((button) => {
      const buttonType = this.getButtonType(button as HTMLElement);

      // Click listener
      const clickListener = () => {
        this.handleButtonClick(buttonType);
      };

      // Keydown listener (Enter/Space)
      const keydownListener = (event: Event) => {
        const keyEvent = event as KeyboardEvent;
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          this.handleButtonClick(buttonType);
        }
      };

      // Attach listeners to actual button element
      button.addEventListener('click', clickListener);
      button.addEventListener('keydown', keydownListener);

      // Store for cleanup
      this.buttonListeners.set(button, {
        click: clickListener,
        keydown: keydownListener,
      });
    });
  }

  private cleanupButtonListeners() {
    this.buttonListeners.forEach((listeners, button) => {
      button.removeEventListener('click', listeners.click);
      button.removeEventListener('keydown', listeners.keydown);
    });
    this.buttonListeners.clear();
  }

  private getButtonType(button: HTMLElement): string {
    if (button.classList.contains('p-datepicker-prev')) return 'prev';
    if (button.classList.contains('p-datepicker-next')) return 'next';
    if (button.classList.contains('p-datepicker-month')) return 'month';
    if (button.classList.contains('p-datepicker-year')) return 'year';
    return 'unknown';
  }

  // ========== HANDLE BUTTON CLICKS ==========
  private handleButtonClick(buttonType: string) {
    // Wait for PrimeNG to update DOM
    setTimeout(() => {
      // Handle based on button type - you can override behavior here
      switch (buttonType) {
        case 'prev':
        case 'next':
          // Month/decade navigation - re-attach listeners and setup grid
          this.attachButtonListeners();
          this.setupGridNavigation();
          break;

        case 'month':
          this.currentView = 'month';
          this.attachButtonListeners();
          this.setupGridNavigation();
          break;

        case 'year':
          this.currentView = 'year';
          this.attachButtonListeners();
          this.setupGridNavigation();
          break;
      }
    }, 100);
  }

  // ========== SETUP GRID NAVIGATION ==========
  private setupGridNavigation() {
    const view = this.detectCurrentView();

    if (!view) {
      return;
    }

    this.currentView = view;
    // Attach listeners to grid cells
    this.attachGridCellListeners();
  }

  private attachGridCellListeners() {
    this.cleanupCellListeners();

    let cells: NodeListOf<Element>;
    switch (this.currentView) {
      case 'date':
        cells = this.calendarElement!.querySelectorAll(
          '.p-datepicker-calendar tbody td span'
        );
        this.focusableElements = Array.from(cells) as HTMLElement[];
        this.setupDateView();
        break;
      case 'month':
        cells = this.calendarElement!.querySelectorAll(
          '.p-monthpicker .p-monthpicker-month'
        );
        this.focusableElements = Array.from(cells) as HTMLElement[];
        this.setupMonthView();
        break;
      case 'year':
        cells = this.calendarElement!.querySelectorAll(
          '.p-yearpicker .p-yearpicker-year'
        );
        this.focusableElements = Array.from(cells) as HTMLElement[];
        this.setupYearView();
        break;
      default:
        return;
    }

    cells.forEach((cell, index) => {
      cell.setAttribute('id', `${this.currentView}-cell-${index}`);
      // Keydown listener for arrow key navigation
      const keydownListener = (event: Event) => {
        this.handleCellKeydown(event as KeyboardEvent);
      };

      // Click listener for when cells are clicked (month/year selection)
      const clickListener = () => {
        this.handleCellClick();
      };

      // Attach both keydown and click listeners to each cell
      cell.addEventListener('keydown', keydownListener, true);
      cell.addEventListener('click', clickListener);

      // Store both listeners for cleanup
      this.cellListeners.set(cell, {
        keydown: keydownListener,
        click: clickListener,
      } as any);
    });
  }

  private cleanupCellListeners() {
    this.cellListeners.forEach((listeners: any, cell) => {
      cell.removeEventListener('keydown', listeners.keydown);
      cell.removeEventListener('click', listeners.click);
    });
    this.cellListeners.clear();
  }

  private detectCurrentView(): 'date' | 'month' | 'year' | null {
    if (!this.calendarElement) return null;

    const monthPicker = this.calendarElement.querySelector(
      '.p-monthpicker'
    ) as HTMLElement;
    const yearPicker = this.calendarElement.querySelector(
      '.p-yearpicker'
    ) as HTMLElement;
    const datePicker = this.calendarElement.querySelector(
      '.p-datepicker-calendar'
    ) as HTMLElement;

    if (
      monthPicker &&
      window.getComputedStyle(monthPicker).display !== 'none'
    ) {
      return 'month';
    }

    if (yearPicker && window.getComputedStyle(yearPicker).display !== 'none') {
      return 'year';
    }

    if (datePicker && window.getComputedStyle(datePicker).display !== 'none') {
      return 'date';
    }

    return null;
  }

  // ========== MAIN KEYDOWN HANDLER ==========
  private handleCellKeydown(event: KeyboardEvent) {
    const { key } = event;

    // Escape closes calendar in all views
    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      const input = this.elementRef.nativeElement.querySelector(
        'input'
      ) as HTMLElement;
      input?.focus();
      return;
    }

    const target = event.target as HTMLElement;

    const navigationKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'PageUp',
      'PageDown',
      'Enter',
      ' ',
    ];

    if (!navigationKeys.includes(key)) return;
    // Route to appropriate handler based on current view
    switch (this.currentView) {
      case 'date':
        this.handleDateNavigation(key, event);
        break;
      case 'month':
        this.handleMonthNavigation(key, event);
        break;
      case 'year':
        this.handleYearNavigation(key, event);
        break;
    }
  }

  private handleCellClick() {
    // Wait for PrimeNG to update DOM after cell click
    setTimeout(() => {
      const currentView = this.detectCurrentView();
      if (currentView) {
        this.currentView = currentView;
        this.attachButtonListeners();
        this.attachGridCellListeners();
      }
    }, 100);
  }

  setupDateView() {
    setTimeout(() => {
      const datePicker: HTMLElement | null =
        document.querySelector('.p-datepicker');
      if (datePicker) {
        datePicker.setAttribute(
          'aria-label',
          this.ariaLabel.length > 0 ? this.ariaLabel : this.placeholder
        );
      }
      const grid: HTMLElement | null = document.querySelector(
        '.p-datepicker-calendar'
      );
      if (!grid) return;

      // Apply ARIA roles
      grid.setAttribute('role', 'grid');
      grid.setAttribute('aria-label', 'Calendar');

      const rows = grid.querySelectorAll('tr');
      rows.forEach((tr, index) => {
        tr.setAttribute('role', 'row');
        tr.setAttribute('aria-rowindex', (index + 1).toString());
      });

      // Get all focusable cells (td elements, not spans)
      const cells: HTMLElement[] = Array.from(
        grid.querySelectorAll('td:not(.p-datepicker-other-month)')
      ) as HTMLElement[];

      if (!cells.length) return;

      this.focusableElements = cells;

      // Get displayed month/year from header
      const monthText =
        document
          .querySelector('.p-datepicker-title .p-datepicker-month')
          ?.textContent?.trim() || '';
      const yearText =
        document
          .querySelector('.p-datepicker-title .p-datepicker-year')
          ?.textContent?.trim() || '';
      const displayedMonthIndex = new Date(
        `${monthText} 1, ${yearText}`
      ).getMonth();
      const displayedYear = parseInt(yearText, 10);

      // Find selected or today cell
      let currentIndex = cells.findIndex((cell) => {
        const span = cell.querySelector('span');
        return (
          span &&
          (span.classList.contains('p-highlight') ||
            span.classList.contains('p-today'))
        );
      });
      if (currentIndex < 0) {
        currentIndex = 0;
        this.currentFocusIndex = 0;
      }

      // Reset all cells
      cells.forEach((cell, index) => {
        const span = cell.querySelector('span');
        if (!span) return;

        const day = parseInt(span.textContent || '1', 10);
        const dateObj = new Date(displayedYear, displayedMonthIndex, day);

        const isDisabled = span.classList.contains('p-disabled');
        cell.setAttribute(
          'aria-label',
          `${dateObj.toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}` + (isDisabled ? ' disabled' : '')
        );

        span.setAttribute('role', 'gridcell');
        span.setAttribute(
          'aria-label',
          `${dateObj.toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}` + (isDisabled ? ' disabled' : '')
        );
        span.setAttribute('aria-selected', 'false');
        if (span) {
          span.setAttribute(
            'tabindex',
            index === this.currentFocusIndex ? '0' : '-1'
          );
          span.setAttribute('aria-selected', 'false');

          if (
            document.activeElement &&
            document.activeElement.tagName !== 'INPUT'
          ) {
            if (index === this.currentFocusIndex) {
              span.setAttribute('aria-selected', 'true');
              span.focus();
            }
          }
        }
        cell.setAttribute('aria-colindex', ((index % 7) + 1).toString());
      });
    }, 100);
  }

  handleDateNavigation(key: string, event: KeyboardEvent) {
    event.preventDefault();
    let newIndex = this.currentFocusIndex;

    switch (event.key) {
      case 'ArrowRight':
        event.stopImmediatePropagation();
        if (newIndex + 1 < this.focusableElements.length) {
          newIndex++;
        } else {
          // Move to next month only if at the very last cell
          this.navigateMonth(1);
          return;
        }
        break;

      case 'ArrowLeft':
        event.stopImmediatePropagation();
        if (newIndex - 1 >= 0) {
          newIndex--;
        } else {
          // Move to previous month only if at the very first cell
          this.navigateMonth(-1);
          return;
        }
        break;

      case 'ArrowDown': {
        event.stopImmediatePropagation();
        const nextIndex = newIndex + 7;
        if (nextIndex < this.focusableElements.length) {
          newIndex = nextIndex;
        } else {
          // Move to next month only if at or past the last row
          this.navigateMonth(1);
          return;
        }
        break;
      }

      case 'ArrowUp': {
        event.stopImmediatePropagation();
        const prevIndex = newIndex - 7;
        if (prevIndex >= 0) {
          newIndex = prevIndex;
        } else {
          // Move to previous month only if at or above the first row
          this.navigateMonth(-1);
          return;
        }
        break;
      }

      case 'Home': {
        // Recalculate row for currentIndex
        const homeRow = Math.floor(newIndex / 7);
        const firstInRow = homeRow * 7;
        if (firstInRow < this.focusableElements.length) {
          newIndex = firstInRow;
        }
        break;
      }

      case 'End': {
        // Recalculate row for currentIndex
        const endRow = Math.floor(newIndex / 7);
        const lastInRow = Math.min(
          endRow * 7 + 6,
          this.focusableElements.length - 1
        );
        if (lastInRow < this.focusableElements.length) {
          newIndex = lastInRow;
        }
        break;
      }

      case 'PageUp':
        if (event.shiftKey) {
          this.navigateYear(-1);
        } else {
          this.navigateMonth(-1);
        }
        break;

      case 'PageDown':
        if (event.shiftKey) {
          this.navigateYear(1);
        } else {
          this.navigateMonth(1);
        }
        break;

      case 'Enter':
      case ' ':
        this.focusableElements[this.currentFocusIndex]?.click();
        break;

      case 'Escape':
        // Close calendar
        const closeButton = document.querySelector(
          '.p-datepicker-close'
        ) as HTMLElement;
        closeButton?.click();
        break;
    }

    if (newIndex !== this.currentFocusIndex) {
      this.currentFocusIndex = newIndex;
      this.focusCurrentDateElement();
    }
  }

  private setupMonthView() {
    setTimeout(() => {
      const calendar = document.querySelector('.p-datepicker');
      if (!calendar) return;
      // Get all month cells
      const monthContainer = document.querySelector('.p-monthpicker');
      if (!monthContainer) return;
      monthContainer.setAttribute('role', 'grid');
      const monthCells = Array.from(
        calendar.querySelectorAll(
          '.p-monthpicker .p-monthpicker-month'
        )
      ) as HTMLElement[];

      this.focusableElements = monthCells;

      // Find current month
      const highlightedIndex = monthCells.findIndex((cell) =>
        cell.classList.contains('p-highlight')
      );

      this.currentFocusIndex = highlightedIndex >= 0 ? highlightedIndex : 0;

      // Setup ARIA attributes
      monthCells.forEach((cell, index) => {
        cell.setAttribute(
          'tabindex',
          index === this.currentFocusIndex ? '0' : '-1'
        );
        cell.setAttribute('role', 'gridcell');
        const isDisabled = cell.classList.contains('p-disabled');
        cell.setAttribute('aria-label', `${cell.textContent?.trim()} month` + (isDisabled ? ' disabled' : ''));

        if (index === this.currentFocusIndex) {
          cell.setAttribute('aria-selected', 'true');
          cell.focus();
        }
      });

      this.announceToScreenReader(
        `Month selector. ${monthCells.length} months available.`
      );
    }, 50);
  }

  private handleMonthNavigation(key: string, event: KeyboardEvent) {
    event.preventDefault();

    let newIndex = this.currentFocusIndex;
    const columns = 3; // Months are in 3 columns

    switch (key) {
      case 'ArrowRight':
        newIndex = Math.min(
          this.currentFocusIndex + 1,
          this.focusableElements.length - 1
        );
        event.stopPropagation();
        event.stopImmediatePropagation();
        break;
      case 'ArrowLeft':
        newIndex = Math.max(this.currentFocusIndex - 1, 0);
        event.stopPropagation();
        event.stopImmediatePropagation();

        break;
      case 'ArrowDown':
        newIndex = Math.min(
          this.currentFocusIndex + columns,
          this.focusableElements.length - 1
        );
        event.stopPropagation();
        event.stopImmediatePropagation();
        break;
      case 'ArrowUp':
        newIndex = Math.max(this.currentFocusIndex - columns, 0);
        event.stopPropagation();
        event.stopImmediatePropagation();
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.focusableElements.length - 1;
        break;
      case 'Enter':
      case ' ':
        this.focusableElements[this.currentFocusIndex]?.click();
        setTimeout(() => this.setupGridNavigation(), 100);
        break;
    }

    if (newIndex !== this.currentFocusIndex) {
      this.currentFocusIndex = newIndex;
      this.focusCurrentElement();
    }
  }

  private setupYearView() {
    setTimeout(() => {
      const calendar = document.querySelector('.p-datepicker');
      if (!calendar) return;
      const yearContainer = document.querySelector('.p-yearpicker');
      if (!yearContainer) return;
      yearContainer.setAttribute('role', 'grid');

      // Get all year cells
      const yearCells = Array.from(
        calendar.querySelectorAll(
          '.p-yearpicker .p-yearpicker-year'
        )
      ) as HTMLElement[];

      this.focusableElements = yearCells;

      // Find current year
      const highlightedIndex = yearCells.findIndex((cell) =>
        cell.classList.contains('p-highlight')
      );

      this.currentFocusIndex = highlightedIndex >= 0 ? highlightedIndex : 0;

      // Setup ARIA attributes
      yearCells.forEach((cell, index) => {
        cell.setAttribute(
          'tabindex',
          index === this.currentFocusIndex ? '0' : '-1'
        );
        cell.setAttribute('role', 'gridcell');
        const isDisabled = cell.classList.contains('p-disabled');
        cell.setAttribute('aria-label', `Year ${cell.textContent?.trim()}` + (isDisabled ? ' disabled' : ''));
      });

      if (yearCells[this.currentFocusIndex]) {
        setTimeout(() => {
          const cell = yearCells[this.currentFocusIndex];
          cell.setAttribute('aria-selected', 'true');
          cell.focus();
        }, 100);
      }

      this.announceToScreenReader(
        `Year selector. ${yearCells.length} years available.`
      );
    }, 100);
  }

  private handleYearNavigation(key: string, event: KeyboardEvent) {
    event.preventDefault();

    let newIndex = this.currentFocusIndex;
    const columns = 2; // Years are in 2 columns

    switch (key) {
      case 'ArrowRight':
        event.stopImmediatePropagation();
        if (newIndex + 1 < this.focusableElements.length) {
          newIndex = Math.min(
            this.currentFocusIndex + 1,
            this.focusableElements.length - 1
          );
        } else {
          // Move to next decade only if at the very last cell
          this.navigateDecade(1);
          return;
        }
        break;
      case 'ArrowLeft':
        event.stopImmediatePropagation();
        if (newIndex - 1 >= 0) {
          newIndex = Math.max(this.currentFocusIndex - 1, 0);
        } else {
          // Move to previous decade only if at the very first cell
          this.navigateDecade(-1);
          return;
        }
        break;
      case 'ArrowDown':
        event.stopImmediatePropagation();
        newIndex = Math.min(
          this.currentFocusIndex + columns,
          this.focusableElements.length - 1
        );
        break;
      case 'ArrowUp':
        event.stopImmediatePropagation();
        newIndex = Math.max(this.currentFocusIndex - columns, 0);
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.focusableElements.length - 1;
        break;
      case 'PageDown':
        // Next decade
        const nextBtn = document.querySelector(
          '.p-yearpicker-next'
        ) as HTMLElement;
        nextBtn?.click();
        setTimeout(() => this.setupGridNavigation(), 100);
        return;
      case 'PageUp':
        // Previous decade
        const prevBtn = document.querySelector(
          '.p-yearpicker-prev'
        ) as HTMLElement;
        prevBtn?.click();
        setTimeout(() => this.setupGridNavigation(), 100);
        return;
      case 'Enter':
      case ' ':
        this.focusableElements[this.currentFocusIndex]?.click();
        setTimeout(() => this.setupGridNavigation(), 100);
        // Will automatically switch back to date view
        break;
    }

    if (newIndex !== this.currentFocusIndex) {
      this.currentFocusIndex = newIndex;
      this.focusCurrentElement();
    }
  }

  // ========== SHARED HELPERS ==========
  private focusCurrentElement() {
    // Remove aria-selected from all
    this.focusableElements.forEach((el, index) => {
      el.setAttribute(
        'tabindex',
        index === this.currentFocusIndex ? '0' : '-1'
      );
      el.removeAttribute('aria-selected');
    });

    // Set on current
    const currentEl = this.focusableElements[this.currentFocusIndex];
    if (currentEl) {
      currentEl.setAttribute('aria-selected', 'true');
      currentEl.focus();

      const label =
        currentEl.getAttribute('aria-label') ||
        currentEl.textContent?.trim() ||
        '';
      this.announceToScreenReader(label);
    }
  }

  focusCurrentDateElement() {
    // Remove aria-selected from all
    this.focusableElements.forEach((el, index) => {
      const span = el.querySelector('span');
      if (span) {
        span.setAttribute(
          'tabindex',
          index === this.currentFocusIndex ? '0' : '-1'
        );
        span.setAttribute('aria-selected', 'false');
      }
    });

    // Set on current
    const currentEl = this.focusableElements[this.currentFocusIndex];
    const currentSpan = currentEl.querySelector('span');
    if (currentSpan) {
      currentSpan.setAttribute('aria-selected', 'true');
      currentSpan.focus();
    }
  }

  private announceToScreenReader(message: string) {
    let liveRegion = document.getElementById('calendar-live-region');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'calendar-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion!.textContent = message;
    }, 10);
  }

  private navigateMonth(direction: number) {
    const button =
      direction > 0
        ? (document.querySelector('.p-datepicker-next') as HTMLElement)
        : (document.querySelector('.p-datepicker-prev') as HTMLElement);

    if (button) {
      button.click();
      setTimeout(() => this.setupGridNavigation(), 50);
    }
  }

  private navigateYear(direction: number) {
    const button =
      direction > 0
        ? (document.querySelector('.p-datepicker-next-year') as HTMLElement)
        : (document.querySelector('.p-datepicker-prev-year') as HTMLElement);

    if (button) {
      button.click();
      setTimeout(() => this.setupGridNavigation(), 50);
    }
  }

  private navigateDecade(direction: number) {
    const button =
      direction > 0
        ? (document.querySelector('.p-datepicker-next') as HTMLElement)
        : (document.querySelector('.p-datepicker-prev') as HTMLElement);

    if (button) {
      setTimeout(() => this.setupGridNavigation(), 50);
    }
  }

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disableControl = isDisabled;
  }

  onValueChange(value: any): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private removeLiveRegion() {
    const liveRegion = document.getElementById('primeng-calendar-live-region');
    if (liveRegion) {
      liveRegion.remove();
    }
  }
}
